/** DISCO companies enabled for code-10 admin — display order: MEPCO, FESCO, IESCO */
export const ENABLED_DISCO_COMPANY_CODES = ["15", "13", "14"];

export const DEFAULT_DISCO_COMPANY_CODE = "15";

export const SELECTED_ADMIN_COMPANY_STORAGE_KEY = "selectedAdminCompanyCode";
export const LAST_ADMIN_COMPANY_PREF_KEY = "lastAdminCompanyPreference";

export const readLastAdminCompanyPreference = () => {
  try {
    return localStorage.getItem(LAST_ADMIN_COMPANY_PREF_KEY) || "";
  } catch {
    return "";
  }
};

export const writeLastAdminCompanyPreference = (companyCode) => {
  try {
    if (companyCode) {
      localStorage.setItem(LAST_ADMIN_COMPANY_PREF_KEY, String(companyCode));
    }
  } catch {
    // ignore storage errors
  }
};

export const readPersistedAdminCompanyCode = () => {
  try {
    return localStorage.getItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

export const writePersistedAdminCompanyCode = (companyCode) => {
  try {
    if (companyCode) {
      localStorage.setItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY, String(companyCode));
      return;
    }
    localStorage.removeItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const clearPersistedAdminCompanyCode = () => {
  writePersistedAdminCompanyCode("");
};

export const ADMIN_COMPANY_SESSION_KEY = "adminCompanySessionActive";
export const ADMIN_COMPANY_PROMPT_PENDING_KEY = "adminCompanyPromptPending";

export const markAdminCompanySessionActive = () => {
  try {
    sessionStorage.setItem(ADMIN_COMPANY_SESSION_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

export const isAdminCompanySessionActive = () => {
  try {
    return sessionStorage.getItem(ADMIN_COMPANY_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearAdminCompanySession = () => {
  try {
    sessionStorage.removeItem(ADMIN_COMPANY_SESSION_KEY);
  } catch {
    // ignore storage errors
  }
};

export const markAdminCompanyPromptPending = () => {
  try {
    sessionStorage.setItem(ADMIN_COMPANY_PROMPT_PENDING_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

export const clearAdminCompanyPromptPending = () => {
  try {
    sessionStorage.removeItem(ADMIN_COMPANY_PROMPT_PENDING_KEY);
  } catch {
    // ignore storage errors
  }
};

export const isAdminCompanyPromptPending = () => {
  try {
    return sessionStorage.getItem(ADMIN_COMPANY_PROMPT_PENDING_KEY) === "1";
  } catch {
    return false;
  }
};

export const isBrowserPageReload = () => {
  try {
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    return navigationEntry?.type === "reload";
  } catch {
    return false;
  }
};

export const clearAdminCompanySelection = () => {
  clearPersistedAdminCompanyCode();
  clearAdminCompanySession();
  clearAdminCompanyPromptPending();
};

export const readRestorableAdminCompanyCode = () => {
  if (!isAdminCompanySessionActive()) {
    return "";
  }
  return readPersistedAdminCompanyCode();
};

export const isEnabledDiscoCompany = (companyCode) =>
  ENABLED_DISCO_COMPANY_CODES.includes(String(companyCode));

/** PITC HQ stays selectable; other DISCOs follow the login allow-list. */
export const isSelectableCompanyDropdownOption = (
  companyCode,
  currentValue,
) => {
  const code = String(companyCode ?? "").trim();
  if (!code) return true;
  if (code === "10" || code === "1") return true;
  if (currentValue != null && String(currentValue).trim() === code) {
    return true;
  }
  return isEnabledDiscoCompany(code);
};

export const sortEnabledDiscoCompanies = (companies = []) => {
  const orderMap = Object.fromEntries(
    ENABLED_DISCO_COMPANY_CODES.map((code, index) => [code, index]),
  );

  return [...companies]
    .filter((company) => isEnabledDiscoCompany(company.companyCode))
    .sort((left, right) => {
      const leftOrder = orderMap[String(left.companyCode)] ?? 999;
      const rightOrder = orderMap[String(right.companyCode)] ?? 999;
      return leftOrder - rightOrder;
    });
};

/** PITC HQ admin roles — company code 10, skip DISCO picker, use office 10 */
const PITC_HQ_ADMIN_ROLE_IDS = new Set(["1151"]);

/** Roles that must pick a DISCO on login (Director), even if office code is not 10 */
const DISCO_COMPANY_PICKER_ROLE_IDS = new Set(["1180"]);

export const isPitcHqAdminRole = (roleId) =>
  PITC_HQ_ADMIN_ROLE_IDS.has(String(roleId));

export const isDiscoCompanyPickerRole = (roleId) =>
  DISCO_COMPANY_PICKER_ROLE_IDS.has(String(roleId));

/** PITC HQ admin — company code 10, role 1151 */
export const isPitcAdminUser = (code, roleId) =>
  String(code) === "10" && isPitcHqAdminRole(roleId);

/** DISCO picker users — code 10 (except 1151), plus Director 1180 */
export const isDiscoAdminUser = (code, roleId) => {
  if (isPitcHqAdminRole(roleId)) return false;
  if (isDiscoCompanyPickerRole(roleId)) return true;
  return String(code) === "10";
};

/**
 * The top-bar DISCO switcher is shown (code 10, except PITC HQ 1151). Pages then follow
 * it and hide their own company select; users without it keep the page select.
 */
export const hasHeaderDiscoSwitcher = (code, roleId) =>
  String(code) === "10" && isDiscoAdminUser(code, roleId);

/** Role / Permission Management screens — HQ (code 10) and Director 1180 */
export const canManageRolesAndPermissions = (code, roleId) =>
  String(code) === "10" || isDiscoCompanyPickerRole(roleId);

export const persistAdminCompanySelectionToStorage = (companyCode) => {
  const code = String(companyCode ?? "").trim();
  if (!code) {
    return;
  }
  writePersistedAdminCompanyCode(code);
  writeLastAdminCompanyPreference(code);
  markAdminCompanySessionActive();
};

/** Company code used for subdivision/API calls after login */
export const getAdminCompanyCodeForData = (code, roleId, selectedAdminCompanyCode) => {
  if (isPitcAdminUser(code, roleId) || isPitcHqAdminRole(roleId)) {
    return 10;
  }
  if (isDiscoAdminUser(code, roleId)) {
    return selectedAdminCompanyCode || null;
  }
  return code;
};

/** Admin must confirm DISCO picker in this browser session before app routes load data. */
export const isAdminDiscoSessionReady = (
  code,
  roleId,
  selectedAdminCompanyCode,
) => {
  if (!isDiscoAdminUser(code, roleId)) {
    return true;
  }
  if (isAdminCompanyPromptPending()) {
    return false;
  }
  if (!isAdminCompanySessionActive()) {
    return false;
  }
  return Boolean(
    getAdminCompanyCodeForData(code, roleId, selectedAdminCompanyCode),
  );
};

/** Admin login-selected DISCO (Redux + localStorage), else empty — never defaults to MEPCO. */
export const resolveAdminSelectedDiscoCode = (
  userCode,
  roleId,
  selectedAdminCompanyCode,
) => {
  const adminCompany =
    selectedAdminCompanyCode || readPersistedAdminCompanyCode();
  const resolved = getAdminCompanyCodeForData(userCode, roleId, adminCompany);
  return resolved ? String(resolved).slice(0, 2) : "";
};

/** officeCode for IBS/API — admin → selected DISCO; normal user → own company code. */
export const resolveAdminDiscoOfficeCode = (
  userCode,
  roleId,
  selectedAdminCompanyCode,
) => {
  if (!isDiscoAdminUser(userCode, roleId)) {
    return String(userCode ?? "").slice(0, 2);
  }
  return resolveAdminSelectedDiscoCode(userCode, roleId, selectedAdminCompanyCode);
};

/** Replace hardcoded MEPCO (15) defaults for code-10 users */
export const resolveOfficeCodeForAdmin = (offCode, selectedAdminCompanyCode) => {
  if (String(offCode) !== "10") {
    return offCode;
  }
  return selectedAdminCompanyCode || offCode;
};

/** Office code for paginated API calls (slices + forms) */
export const resolveApiOfficeCode = (
  code,
  roleId,
  officeCodeParam,
  selectedAdminCompanyCode,
) => {
  if (!isDiscoAdminUser(code, roleId) && !isPitcAdminUser(code, roleId)) {
    return code;
  }
  // Prefer explicit office / login-selected DISCO (e.g. FESCO 13) over HQ code 10
  const scoped =
    officeCodeParam != null && String(officeCodeParam).trim() !== ""
      ? officeCodeParam
      : selectedAdminCompanyCode;
  if (scoped != null && String(scoped).trim() !== "" && String(scoped) !== "10") {
    return scoped;
  }
  if (isPitcAdminUser(code, roleId) || isPitcHqAdminRole(roleId)) {
    return 10;
  }
  return selectedAdminCompanyCode || code;
};

/** Login CFC / hierarchy level for table APIs (prefer cfcLevelId from SignInMFA). */
export const resolveLoginOfficeLevel = (hierarchy, cfcLevelId) => {
  const fromCfc = Number(cfcLevelId);
  if (Number.isFinite(fromCfc) && fromCfc > 0) return fromCfc;
  const fromHierarchy = Number(hierarchy);
  if (Number.isFinite(fromHierarchy) && fromHierarchy > 0) return fromHierarchy;
  return 2;
};

/** Derive API officeLevel from a company / circle / division / subdivision code. */
export const officeLevelFromOfficeCode = (officeCode, fallbackHierarchy) => {
  const fallback = Number(fallbackHierarchy);
  const hasValidFallback = Number.isFinite(fallback) && fallback > 0;
  const digits = String(officeCode ?? "").replace(/\D/g, "");
  // CSC / extended office codes — use login level, not digit count (7-digit CSC ≠ level 5).
  if (digits.length >= 6 && hasValidFallback) {
    return fallback;
  }
  if (digits.length >= 5) return 5;
  if (digits.length === 4) return 4;
  if (digits.length === 3) return 3;
  if (digits.length === 2) return 2;
  return hasValidFallback ? fallback : 2;
};

/**
 * officeLevel for paginated getAllApplications-style APIs.
 * Scoped to the user's login office → cfcLevelId / hierarchy; admin picks → code length.
 */
export const resolveTableApiOfficeLevel = ({
  userCode,
  effectiveOfficeCode,
  hierarchy,
  cfcLevelId,
  explicitOfficeLevel,
}) => {
  const loginOfficeLevel =
    String(userCode) === "10" ? 2 : resolveLoginOfficeLevel(hierarchy, cfcLevelId);
  const levelFallback =
    explicitOfficeLevel != null && explicitOfficeLevel !== ""
      ? explicitOfficeLevel
      : loginOfficeLevel;

  if (
    String(userCode) !== "10" &&
    String(effectiveOfficeCode ?? "") === String(userCode ?? "")
  ) {
    return loginOfficeLevel;
  }

  return officeLevelFromOfficeCode(effectiveOfficeCode, levelFallback);
};

/**
 * Prefer an explicitly selected table office (subdivision, division, …)
 * over the login office, including for non-admin users.
 */
export const resolveScopedTableOfficeCode = (
  code,
  roleId,
  officeCodeParam,
  selectedAdminCompanyCode,
) => {
  if (officeCodeParam != null && String(officeCodeParam).trim() !== "") {
    return officeCodeParam;
  }
  return resolveApiOfficeCode(
    code,
    roleId,
    officeCodeParam,
    selectedAdminCompanyCode,
  );
};

/** Default company dropdown value for code-10 admin screens */
export const getInitialAdminOfficeCode = (code, selectedAdminCompanyCode, roleId) => {
  if (isDiscoAdminUser(code, roleId) || String(code) === "10") {
    const fromRedux = selectedAdminCompanyCode
      ? String(selectedAdminCompanyCode)
      : "";
    if (fromRedux && fromRedux !== "10") return fromRedux;
    const persisted = readPersistedAdminCompanyCode();
    if (persisted && persisted !== "10") return String(persisted);
    return fromRedux;
  }
  return String(code ?? "");
};

/** Code-10 admin users must not load billing service table data */
export const isBillingTableFetchEnabled = (code) => String(code) !== "10";

/** Selected company for code-10 filters (dropdown, reports, tables) */
export const getEffectiveAdminCompanyCode = (
  offCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
) => {
  if (String(offCode) !== "10") {
    return offCode;
  }
  return selectedCompanyCode || selectedAdminCompanyCode || offCode;
};

/** Office code for report Go / API — respects hierarchy filters for code-10 admin */
export const resolveReportGoOfficeCode = ({
  code,
  officeCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
  circleCode,
  selectedDivision,
  selectedSubDivision,
}) => {
  if (String(code) !== "10") {
    return officeCode;
  }
  if (selectedSubDivision) return selectedSubDivision;
  if (selectedDivision) return selectedDivision;
  if (circleCode) return circleCode;
  const companyCode = getEffectiveAdminCompanyCode(
    code,
    selectedCompanyCode,
    selectedAdminCompanyCode,
  );
  return companyCode || officeCode;
};

/** CFC dashboard API — 2-digit company code → regional CFC id (e.g. 15 → 1500001) */
export const resolveCfcDashboardOfficeCode = (
  officeCodeParam,
  code,
  roleId,
  selectedAdminCompanyCode,
) => {
  let officeCode = officeCodeParam;
  if (String(code) === "10" && String(officeCode) === "10") {
    officeCode =
      getAdminCompanyCodeForData(code, roleId, selectedAdminCompanyCode) ||
      officeCode;
  }
  if (String(officeCode).length <= 2) {
    return `${String(officeCode).padStart(2, "0")}00001`;
  }
  return officeCode;
};

const DISCO_SHORT_NAMES = {
  11: "LESCO",
  12: "GEPCO",
  13: "FESCO",
  14: "IESCO",
  15: "MEPCO",
  26: "PESCO",
  27: "HAZECO",
  37: "HESCO",
  38: "SEPCO",
  48: "QESCO",
  59: "TESCO",
};

/** 2-digit DISCO code for logos/watermarks (admin code 10 → selected DISCO). */
export const resolveDiscoCompanyCodeForBranding = (
  userCode,
  roleId,
  selectedAdminCompanyCode,
) => {
  const resolved = String(
    getAdminCompanyCodeForData(userCode, roleId, selectedAdminCompanyCode) ??
      userCode,
  );
  if (resolved === "10") {
    return String(selectedAdminCompanyCode || DEFAULT_DISCO_COMPANY_CODE).slice(
      0,
      2,
    );
  }
  return resolved.slice(0, 2);
};

/** Extract 2-digit DISCO code from application id (e.g. CA-14111260616006 → 14). */
export const extractDiscoCodeFromApplicationId = (applicationId) => {
  if (!applicationId) return "";

  const afterHyphen = String(applicationId).trim().split("-")[1];
  if (afterHyphen && afterHyphen.length >= 2) {
    return afterHyphen.slice(0, 2);
  }

  return String(applicationId).slice(3, 5);
};

/**
 * DISCO code for document branding (logo, watermark, company name).
 * Admin code 10 → from application id; all other users → existing fallback.
 */
export const resolveDiscoCodeForDocument = (
  applicationId,
  userCode,
  roleId,
  selectedAdminCompanyCode,
  fallbackCompanyCode = "",
) => {
  if (String(userCode) === "10") {
    const discoFromApplication =
      extractDiscoCodeFromApplicationId(applicationId);
    if (discoFromApplication) {
      return discoFromApplication;
    }
  }

  if (
    fallbackCompanyCode !== null &&
    fallbackCompanyCode !== undefined &&
    fallbackCompanyCode !== ""
  ) {
    return String(fallbackCompanyCode).slice(0, 2);
  }

  return resolveDiscoCompanyCodeForBranding(
    userCode,
    roleId,
    selectedAdminCompanyCode,
  );
};

/** First 2 digits of sub-division code → DISCO company code (e.g. 13142 → 13 FESCO). */
export const extractDiscoCodeFromSubDivisionCode = (subDivision) => {
  const digits = String(subDivision ?? "").replace(/\D/g, "");
  return digits.length >= 2 ? digits.slice(0, 2) : "";
};

/** Canvas/PDF overlay label for uploaded documents. */
export const getDiscoUseOnlyWatermarkText = (companyCode) => {
  const discoCode = String(companyCode ?? "").slice(0, 2);
  const shortName = DISCO_SHORT_NAMES[discoCode] || "DISCO";
  return `For ${shortName} Use Only`;
};
